import { useState } from "react";
import { Plus, Search, Filter, Edit2, FileText, Globe } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { initialPages } from "../../lib/mockData";
import { ca } from "date-fns/locale";

const templates = [
  "All Templates",
  "Homepage",
  "Solutions",
  "Products",
  "Services",
  "Industries",
  "About",
  "Contact Us",
  "Landing",
  "Listing",
  "Overview",
  "Legal",
  "Footer",
];
const statuses = ["All Status", "published", "draft", "scheduled"];

const ITEMS_PER_PAGE = 10;

export function PagesList() {
  const navigate = useNavigate();
  const [pages, setPages] = useState(initialPages);
  const [search, setSearch] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("All Templates");
  const [selectedStatus, setSelectedStatus] = useState("All Status");
  const [currentPage, setCurrentPage] = useState(1);

  const filtered = pages.filter(
    (p) =>
      p.title.toLowerCase().includes(search.toLowerCase()) &&
      (selectedTemplate === "All Templates" ||
        p.template === selectedTemplate) &&
      (selectedStatus === "All Status" || p.status === selectedStatus),
  );

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setCurrentPage(1);
  };

  const getEditRoute = (p) => {
    switch (p.template) {
      case "Homepage":
        return "/homeCMS";
      case "Solutions":
        return "/solutionCMS";
      case "Products":
        return "/productCMS";
      case "Services":
        return "/serviceCMS";
      case "Industries":
        return "/industriesCMS";
      case "licensing_calculator":
        return "/licensing_calculator";
      case "estimator_config":
        return "/estimator_config";
      case "Resources":
        return "/resourcesCMS";
      case "About":
        return "/aboutCMS";
      case "Career":
        return "/careersCMS";
      case "Methodology":
        return "/methodologyCMS";
      case "Partners":
        return "/partnersCMS";
      case "ChangePartner":
        return "/changePartnersCMS";
      case "FAQ":
        return "/faqCMS";
      case "Guides":
        return "/guidesCMS";
      case "Contact":
        return "/contactCMS";
      case "Footer":
        return "/footerCMS";
      case "Privacy":
        return "/privacyCMS";
      case "Terms":
        return "/termsCMS";
      default:
        return `/pages/${p.id}/edit`;
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto space-y-5">
      <div className="page-header">
        <div>
          <h1 className="page-title">Pages</h1>
          <p className="page-subtitle">
            {pages.length} pages total · Manage structured template-based
            content
          </p>
        </div>
        <button className="btn-accent" onClick={() => navigate("/pages/new")}>
          <Plus className="w-4 h-4" />
          Create Page
        </button>
      </div>

      <div className="cms-card p-4 flex flex-wrap items-center gap-3">
        <div className="search-input flex-1 min-w-[200px] max-w-sm">
          <Search className="w-4 h-4" />
          <input
            value={search}
            onChange={handleFilterChange(setSearch)}
            placeholder="Search pages..."
            className="flex-1 bg-transparent outline-none text-sm text-foreground placeholder:text-muted-foreground"
          />
        </div>
        <select
          value={selectedTemplate}
          onChange={handleFilterChange(setSelectedTemplate)}
          className="text-sm border border-border rounded-lg px-3 py-2 bg-background text-foreground outline-none cursor-pointer"
        >
          {templates.map((t) => (
            <option key={t}>{t}</option>
          ))}
        </select>
        <select
          value={selectedStatus}
          onChange={handleFilterChange(setSelectedStatus)}
          className="text-sm border border-border rounded-lg px-3 py-2 bg-background text-foreground outline-none cursor-pointer"
        >
          {statuses.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
        <button className="flex items-center gap-2 text-sm text-muted-foreground border border-border rounded-lg px-3 py-2 hover:bg-muted transition-colors">
          <Filter className="w-4 h-4" />
          More filters
        </button>
      </div>

      <div className="section-panel">
        <div className="overflow-x-auto">
          <table className="w-full cms-table">
            <thead>
              <tr>
                <th>Page Title</th>
                <th>Slug</th>
                <th>Template</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={4}>
                    <div className="empty-state">
                      <div className="empty-state-icon">
                        <FileText className="w-6 h-6 text-muted-foreground" />
                      </div>
                      <p className="text-sm font-medium text-foreground">
                        No pages found
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                paginated.map((p) => (
                  <tr key={p.id} className="group">
                    <td>
                      <div className="flex items-center gap-2.5">
                        <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ background: "hsl(var(--muted))" }}
                        >
                          <FileText className="w-4 h-4 text-muted-foreground" />
                        </div>
                        <span className="font-semibold text-foreground">
                          {p.title}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className="flex items-center gap-1 text-muted-foreground font-mono text-xs">
                        <Globe className="w-3.5 h-3.5" />
                        {p.slug}
                      </span>
                    </td>
                    <td>
                      <span
                        className="text-xs font-medium px-2.5 py-1 rounded-full"
                        style={{
                          background: "hsl(var(--info-muted))",
                          color: "hsl(var(--info))",
                        }}
                      >
                        {p.template}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center gap-1 opacity-100 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => navigate(getEditRoute(p))}
                          className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-border">
          <p className="text-xs text-muted-foreground">
            Showing{" "}
            {filtered.length === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1}
            –{Math.min(currentPage * ITEMS_PER_PAGE, filtered.length)} of{" "}
            {filtered.length} pages
          </p>
          <div className="flex items-center gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                onClick={() => setCurrentPage(n)}
                className={`w-8 h-8 rounded-lg text-xs font-medium transition-colors ${
                  n === currentPage
                    ? "text-white"
                    : "text-muted-foreground hover:bg-muted"
                }`}
                style={
                  n === currentPage ? { background: "hsl(var(--accent))" } : {}
                }
              >
                {n}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
